import * as moment from 'moment';
import { useCallback, useMemo } from 'react';
import { IConnectToDonorStatus, IDonorConnect } from '@nkr/models/index'
import { GridWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import { CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, RowDataChangedEvent } from 'ag-grid-community';
import { FormControl, Label } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';
import { ICounselor } from './models';

interface IProps {
    data: IDonorConnect[],
    statusList: IConnectToDonorStatus[],
    userList: ICounselor[],
    onView: (id: number) => void,
    onStatusChange: (id: number, statusId: number) => void,
    onAssignedCounselorChange: (id: number, counselorId: number) => void,
}

const IldaGrid = (props: IProps) => {
    const onGridReady = useCallback((e: GridReadyEvent) => {
        setTimeout(() => e.api.sizeColumnsToFit(), 250);
    }, []);

    const onFirstDataRendered = useCallback((e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }, []);

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        // e.api.sizeColumnsToFit();
    };

    const columns: ColDef[] = [
        {
            colId: 'entrySource',
            field: 'entrySource',
            headerName: 'Source',
            width: 140,
        },

        {
            colId: 'firstName',
            field: 'firstName',
            headerName: 'First Name',
            width: 120,
        },

        {
            colId: 'lastName',
            field: 'lastName',
            headerName: 'Last Name',
            width: 120,
        },

        {
            colId: 'phone',
            headerName: 'Phone',
            width: 110,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;
                return data.phoneNumber != '' ? <>{data.phoneNumber}</> : <>--</>;
            }
        },

        {
            colId: 'candidateStatus',
            field: 'candidateStatus',
            headerName: 'Candidate Status',
            width: 170
        },

        {
            colId: 'connectToDonorNextFollowUpDate',
            field: 'connectToDonorNextFollowUpDate',
            headerName: 'Next Follow-Up',
            width: 155,
            cellRendererParams: { statusList: props.statusList },
            cellRendererFramework: (params: ICellRendererParams & { statusList: IConnectToDonorStatus[] }) => {
                let data = params.data as IDonorConnect;

                let status = params.statusList.find(s => s.id == data.connectToDonorStatusId);

                if (status != null) {
                    let nextFollowUpDate = moment(data.connectToDonorNextFollowUpDate, 'MM/DD/YYYY', true);

                    if (status.isClosed == false && nextFollowUpDate.isBefore(moment(), 'day')) {
                        return <Label bsStyle='danger' style={{ fontSize: '100%' }}>{data.connectToDonorNextFollowUpDate}</Label>
                    }

                    if (status.isClosed == false && nextFollowUpDate.isAfter(moment(), 'day')) {
                        return <Label bsStyle='success' style={{ fontSize: '100%' }}>{data.connectToDonorNextFollowUpDate}</Label>
                    }

                    if (status.isClosed == false && nextFollowUpDate.isSame(moment(), 'day')) {
                        return <Label bsStyle='warning' style={{ fontSize: '100%' }}>{data.connectToDonorNextFollowUpDate}</Label>
                    }

                    if (status.isClosed) {
                        return <Label style={{ fontSize: '100%' }}>{data.connectToDonorNextFollowUpDate}</Label>
                    }
                }

                return null;
            },
            comparator: Utility.dateComparator
        },

        {
            colId: 'connectToDonorStatus',
            field: 'connectToDonorStatus',
            headerName: 'Status',
            width: 240,
            sortable: false,
            cellClass: 'ilda-drop-down',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;

                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            value={data.connectToDonorStatusId}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onStatusChange(data.id, parseInt(e.currentTarget.value))}
                        >
                            {props.statusList.map(item =>
                                <option key={'st-' + item.id} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                    </>
                );
            }
        },

        {
            colId: 'connectToDonorAssignedUserId',
            field: 'connectToDonorAssignedUserId',
            headerName: 'Assigned Counselor',
            width: 240,
            sortable: false,
            cellClass: 'ilda-drop-down',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;

                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            value={data.connectToDonorAssignedUserId}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onAssignedCounselorChange(data.id, parseInt(e.currentTarget.value))}
                        >
                            <option value={0}>- Not Assigned -</option>
                            {props.userList.map(item =>
                                <option key={'au-' + item.id} value={item.id}>{item.firstName} {item.lastName} ({item.activeCaseCount}/{item.donorConnectCaseLoad})</option>
                            )}
                        </FormControl>
                    </>
                );
            }
        },

        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Selected Center',
            width: 140
        }
    ];

    const columnDefs = useMemo(() => columns, [props.statusList]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    sortable: true,
                    suppressMenu: true,
                }}
                rowData={rowData}
                headerHeight={32}
                rowHeight={35}
                suppressCellSelection={true}
                // immutableData={true}
                noRowsOverlayComponentFramework={() => <div>No registrations found...</div>}
                // getRowNodeId={(e: IDonorConnect) => e.id.toString()}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onCellClicked={(params: CellClickedEvent) => {
                    let colDef = params.column.getColDef();

                    if (colDef.colId != 'connectToDonorStatus' && colDef.colId != 'connectToDonorAssignedUserId') {
                        let { id } = (params.data as IDonorConnect);
                        props.onView(id);
                    }
                }}
            />
        </GridWrapper>
    );
}

export default IldaGrid;